import ContentWrapper from "@/components/Common/Containers/ContentContainer";
import Tooltip from "@/components/Common/Tooltip";
import {
  useGetBeneficiaryPerformanceQuery,
  useGetIndividualBeneficiaryQuery,
} from "@/store/beneficiaryApiSlice";
import ArrowLeft from "@assets/arrow-left-long.svg";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import AccountOverview from "./AccountOverview";
import AccountPerformance from "./AccountPerformance";
import AccountTransactions from "./AccountTransactions";
//import CombinedDropdown from "../Common/Input/CombinedDropdown";

const ManageAccount: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const accountNumber = pathname.split("/")[2];

  enum TabName {
    Overview = "Overview",
    Transactions = "Transactions",
    Performance = "Performance",
  }

  const [activeTab, setActiveTab] = useState<TabName>(TabName.Overview);

  enum TransactionsTabName {
    All = "All sources",
    Personal = "Personal",
    Employer = "Employer",
  }

  /* const longTermOptions = [
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Yearly", value: "yearly" },
    { label: "All time", value: "all" },
  ];

  const monthlyOptions = [
    { label: "January", value: "January" },
    { label: "February", value: "February" },
    { label: "March", value: "March" },
    { label: "April", value: "April" },
    { label: "May", value: "May" },
    { label: "June", value: "June" },
    { label: "July", value: "July" },
    { label: "August", value: "August" },
    { label: "September", value: "September" },
    { label: "October", value: "October" },
    { label: "November", value: "November" },
    { label: "December", value: "December" },
  ]; */

  const [activeTransactionTab, setActiveTransactionTab] =
    useState<TransactionsTabName>(TransactionsTabName.All);

  const {
    data: beneficiaryInvestmentSummary,
    isLoading: isBenefitSummaryLoading,
  } = useGetIndividualBeneficiaryQuery(accountNumber, {
    skip: !accountNumber,
    refetchOnReconnect: true,
    pollingInterval: 300000,
  });

  const { data: performanceData, isLoading: isPerformanceDataLoading } =
    useGetBeneficiaryPerformanceQuery(
      {
        userAccountId: beneficiaryInvestmentSummary?.userAccountId ?? "",
        minorAccountId: beneficiaryInvestmentSummary?.minorAccountId ?? "",
      },
      {
        skip:
          !beneficiaryInvestmentSummary?.userAccountId ||
          !beneficiaryInvestmentSummary?.minorAccountId,
        refetchOnReconnect: true,
        pollingInterval: 300000,
      },
    );

  const handleTabClick = (tabName: TabName) => {
    setActiveTab(tabName);
  };

  const calculateReturnGain = () => {
    if (
      !beneficiaryInvestmentSummary ||
      beneficiaryInvestmentSummary.marketValueAsOfDate === undefined ||
      beneficiaryInvestmentSummary.totalInvestmentAmount === undefined
    ) {
      return "0.00";
    }

    const currentBalance =
      beneficiaryInvestmentSummary.marketValueAsOfDate ?? 0;
    const totalInvestmentAmount =
      beneficiaryInvestmentSummary.totalInvestmentAmount ?? 0;

    if (totalInvestmentAmount === 0) {
      return "0.00";
    }

    let result =
      ((currentBalance - totalInvestmentAmount) / totalInvestmentAmount) * 100;

    if (Object.is(result, -0) || Math.abs(result) < 1e-10) {
      result = 0;
    }

    return result.toFixed(2);
  };

  return (
    <>
      <ContentWrapper>
        {isBenefitSummaryLoading || isPerformanceDataLoading ? (
          <>
            <div className="w-full font-outfit flex justify-center items-center h-[80vh]">
              <svg
                className="text-teal-100 animate-spin"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
              >
                <path
                  d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-teal-500"
                ></path>
              </svg>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white font-outfit">
              <div className="w-full md:w-[945px] mx-auto flex justify-between items-center p-4 m-auto">
                <div className="flex items-center space-x-[16px]">
                  <button
                    className="flex space-x-2 items-center shadow-backBtn px-[16px] py-[11px] lg:py-[6px] border-[1px] border-[#E2E2E2] bg-white rounded-[8px]"
                    onClick={() => navigate(-1)}
                  >
                    <img src={ArrowLeft} alt="Arrow Left" />
                    <p className="hidden lg:block">
                      {t("manage_account.back")}
                    </p>
                  </button>
                  <p className="hidden md:block font-medium text-[18px] leading-[127%]">
                    {beneficiaryInvestmentSummary?.minorFirstName}{" "}
                    {beneficiaryInvestmentSummary?.minorLastName}
                    {t("manage_account.account_suffix")}
                  </p>
                </div>

                {/*  <div className="flex items-center justify-end">
                  <CombinedDropdown
                    leftOptions={longTermOptions}
                    rightOptions={monthlyOptions}
                    leftValue={filterLongTermOption}
                    rightValue={filterMonth}
                    onLeftChange={setFilterLongTermOption}
                    onRightChange={setFilterMonth}
                    leftPlaceholder="Filter"
                    rightPlaceholder="Month"
                    // className="w-[250px]"
                  />
                </div> */}
              </div>
            </div>

            <div
              className={`${
                activeTab === "Overview"
                  ? "from-[#EEFCFC]"
                  : activeTab === "Transactions"
                    ? activeTransactionTab === "All sources"
                      ? "from-[#EEFCFC]"
                      : activeTransactionTab === "Personal"
                        ? "from-[#E8F8FF]"
                        : "from-[#FFF6ED]"
                    : "from-[#EEFCFC]"
              } font-outfit bg-gradient-to-b  to-custom-purple `}
            >
              <div className="w-full lg:w-[945px] mx-auto   m-auto py-[24px] md:py-[62px]">
                <p className="block md:hidden px-4 font-medium text-[24px] leading-[127%] mb-[24px]">
                  {beneficiaryInvestmentSummary?.minorFirstName}{" "}
                  {beneficiaryInvestmentSummary?.minorLastName}
                  {t("manage_account.account_suffix")}
                </p>
                <div className="px-4 grid grid-cols-2 md:grid-cols-3 gap-[24px]">
                  <div className="">
                    <p className="text-black">
                      {t("manage_account.summary.total_invested_amount")}
                    </p>
                    <p className="text-[24px] md:text-[32px] font-semibold mt-2">
                      ${" "}
                      {Math.trunc(
                        beneficiaryInvestmentSummary?.totalInvestmentAmount ??
                          0,
                      )}
                    </p>
                  </div>

                  <div className="">
                    <p className=" text-black">
                      {t("manage_account.summary.current_total_balance")}
                    </p>

                    <p className="text-[24px] md:text-[32px] font-semibold mt-2">
                      ${" "}
                      {Math.trunc(
                        beneficiaryInvestmentSummary?.marketValueAsOfDate ?? 0,
                      )}
                    </p>
                  </div>

                  <div className="">
                    <div className="flex space-x-1">
                      <p className=" text-black">
                        {t("manage_account.summary.return")}
                      </p>
                      <Tooltip
                        text={t("manage_account.summary.return_tooltip")}
                      />
                    </div>
                    <p className="text-[24px] md:text-[32px] font-semibold mt-2">
                      {calculateReturnGain()}%
                    </p>
                  </div>
                </div>

                <div className="mt-[32px] md:mt-[50px]  bg-white lg:rounded-t-[8px] md:rounded-t-[24px] lg:px-4 lg:py-[24px] md:p-[24px]">
                  <div className="lg:w-fit w-full flex justify-between  bg-white md:rounded-[54px]   text-[16px] leading-[127%] font-normal">
                    <button
                      style={{
                        boxShadow:
                          TabName.Overview === activeTab
                            ? styles.activeTabStyles
                            : styles.inactiveTabStyles,
                      }}
                      className={`${
                        styles.default
                      } lg:rounded-l-[54px] border-r-[0.5px] border-y-[1px] border-l-[1px] border-[#E2E2E2] ${
                        TabName.Overview === activeTab && "bg-[#F9F9F9]"
                      }`}
                      onClick={() => handleTabClick(TabName.Overview)}
                    >
                      {t("manage_account.tabs.overview")}
                    </button>
                    <button
                      style={{
                        boxShadow:
                          TabName.Transactions === activeTab
                            ? styles.activeTabStyles
                            : styles.inactiveTabStyles,
                      }}
                      className={`${styles.default}  border-y-[1px] ${
                        TabName.Transactions === activeTab && "bg-[#F9F9F9]"
                      }`}
                      onClick={() => handleTabClick(TabName.Transactions)}
                    >
                      {t("manage_account.tabs.transactions")}
                    </button>
                    <button
                      style={{
                        boxShadow:
                          TabName.Performance === activeTab
                            ? styles.activeTabStyles
                            : styles.inactiveTabStyles,
                      }}
                      className={`${
                        styles.default
                      } lg:rounded-r-[54px] border-l-[0.5px] border-y-[1px] border-r-[1px]  border-[#E2E2E2] ${
                        TabName.Performance === activeTab && "bg-[#F9F9F9]"
                      }`}
                      onClick={() => handleTabClick(TabName.Performance)}
                    >
                      {t("manage_account.tabs.performance")}
                    </button>
                  </div>

                  <div className="py-[24px] px-4 lg:px-2  w-full flex flex-col md:flex-row">
                    {/**Leo TO-DO => Change the width back to 60% when you have actions */}
                    <div className="w-full">
                      {activeTab === TabName.Overview && (
                        <AccountOverview
                          //beneficiaryData={beneficiaryInvestmentSummary!}
                          beneficiaryData={performanceData!}
                        />
                      )}
                      {activeTab === TabName.Transactions && (
                        <AccountTransactions
                          //beneficiaryData={beneficiaryInvestmentSummary!}
                          beneficiaryData={performanceData!}
                          beneficiaryInfo={beneficiaryInvestmentSummary}
                          activeTransactionTab={activeTransactionTab}
                          setActiveTransactionTab={setActiveTransactionTab}
                        />
                      )}
                      {activeTab === TabName.Performance && (
                        <AccountPerformance
                          //beneficiaryData={beneficiaryInvestmentSummary!}
                          beneficiaryData={performanceData!}
                        />
                      )}

                      {/* <button
                        className="w-full text-[#00A6AD] text-left mt-[20px] py-3 flex items-center"
                        onClick={() => navigate("/savings-settings")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                        </svg>
                        Account settings
                      </button> */}
                    </div>
                    {/* <div className="w-full md:w-[56px]"></div>
                <div className="w-full md:w-[40%] ">
                  <div className="relative border-[1px] border-[#E2E2E2] rounded-[16px]">
                   
                    <div className="p-[24px]">
                      <button className="w-full font-semibold items-center justify-center flex space-x-2 bg-brand-default text-white rounded-[12px] border-t-[2px] border-[#59CAC9] px-[32px] py-[12px]">
                        <img src={Plus} alt="Plus" />
                        <p>Add Money</p>
                      </button>
                    </div>
                    <div className="w-full border-[0.5px]"></div>
                    <div className="p-[24px]">
                      <p className="text-[16px] mb-[16px]">
                        Share this link with friends and family to receive
                        gifts:
                      </p>
                      <div className="flex items-center bg-gray-100 rounded-lg p-4 mb-4">
                        <span className="flex-grow 0 2px 2px 0 rgba(0, 0, 0, 0.02), 0 -2ptext-md">
                          sootchy.com/34f_gst_2
                        </span>
                        <button className="ml-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              x="9"
                              y="9"
                              width="13"
                              height="13"
                              rx="2"
                              ry="2"
                            ></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>

                      <button className="w-full text-left py-3 flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <circle cx="18" cy="5" r="3"></circle>
                          <circle cx="6" cy="12" r="3"></circle>
                          <circle cx="18" cy="19" r="3"></circle>
                          <line
                            x1="8.59"
                            y1="13.51"
                            x2="15.42"
                            y2="17.49"
                          ></line>
                          <line
                            x1="15.41"
                            y1="6.51"
                            x2="8.59"
                            y2="10.49"
                          ></line>
                        </svg>
                        Share on social networks
                      </button>
                    </div>
                    <div className="w-full border-[0.5px]"></div>
                    <div className="p-[24px]">
                      <button className="w-full text-left py-3 flex items-center ">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        Set-up recurring payments
                      </button>
                      <button
                        className="w-full text-left py-3 flex items-center"
                        onClick={() => navigate("/savings-settings")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                        </svg>
                        Account settings
                      </button>
                    </div>}
                  </div>
                </div> */}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </ContentWrapper>
    </>
  );
};

const styles = {
  default: "w-full px-[16px] py-[16px] lg:py-[10px]",
  activeTabStyles: "0px 1px 3px 0px rgba(0, 0, 0, 0.07) inset",
  inactiveTabStyles:
    "0px 2px 2px 0px rgba(0, 0, 0, 0.02), 0px -2px 1px 0px rgba(0, 0, 0, 0.07) inset",
};

export default ManageAccount;
